import type { FC } from "react";

import { m } from "@recobra/i18n/messages";
import { Button } from "@recobra/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@recobra/ui/dialog";
import { Separator } from "@recobra/ui/separator";

import { useTaskName } from "./task-data";
import { taskStatusLabel } from "./task-display";
import { TaskStatusIcon } from "./task-display";
import { TaskCreationContextHeading } from "./task-form";
import { TaskCreationForm } from "./task-form";
import {
  type TaskCreationFormOptions,
  type TaskFormContext,
  useTaskCreationForm,
} from "./task-form";

export const TaskCreationModal: FC<{
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  context: TaskFormContext;
  initialTaskType?: string;
}> = ({ isOpen, onOpenChange, context, initialTaskType }) => (
  <Dialog open={isOpen} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-lg">
      <TaskCreationDialogBody
        isOpen={isOpen}
        context={context}
        taskTypes={initialTaskType ? [initialTaskType] : undefined}
        initialTaskType={initialTaskType}
        onOpenChange={onOpenChange}
      />
    </DialogContent>
  </Dialog>
);

const TaskCreationDialogBody: FC<
  TaskCreationFormOptions & { isOpen: boolean; taskTypes?: readonly string[] }
> = ({ isOpen, taskTypes, ...formOptions }) => {
  const taskName = useTaskName();
  const { form, context, onModalOpenChange } = useTaskCreationForm(formOptions);
  const formId = "task-creation-form";

  return (
    <>
      <DialogHeader>
        <div className="flex min-w-0 items-center justify-between gap-2 pr-8">
          <form.Subscribe selector={(state) => state.values.taskType}>
            {(taskType) => (
              <DialogTitle>{taskType ? taskName(taskType) : m.task_modal_add_title()}</DialogTitle>
            )}
          </form.Subscribe>
          <span className="flex items-center gap-1.5 text-sm">
            <TaskStatusIcon status="todo" />
            {taskStatusLabel("todo")}
          </span>
        </div>

        <TaskCreationContextHeading
          isOpen={isOpen}
          contextType={context.contextType}
          contextId={context.contextId}
        />
      </DialogHeader>
      <Separator />

      <div className="-mx-4 max-h-[60vh] overflow-y-auto px-4">
        <TaskCreationForm
          form={form}
          formId={formId}
          isOpen={isOpen}
          taskTypes={taskTypes}
          context={context}
        />
      </div>

      <form.Subscribe
        selector={(state) => ({
          taskType: state.values.taskType,
          isSubmitting: state.isSubmitting,
        })}
      >
        {({ taskType, isSubmitting }) => (
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onModalOpenChange(false)}
              disabled={isSubmitting}
            >
              {m.task_cancel()}
            </Button>
            <Button type="submit" form={formId} disabled={!taskType || isSubmitting}>
              {isSubmitting ? m.task_saving() : m.task_create()}
            </Button>
          </DialogFooter>
        )}
      </form.Subscribe>
    </>
  );
};
